import styled from '@emotion/styled';
import { Ripple, Badge } from '@taleemabad/dsm/components';
import { Icon } from '../../atoms/icon/icon';
import { css } from '@emotion/react';
import { Typography } from '../../atoms/typography/typography';
import { PropsWithChildren, ReactNode } from 'react';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { Can, useAccessControlAbility } from '@taleemabad/shared';

export interface SideNavBarItem {
  label: string;
  analyticsKey: string;
  icon: string;
  id: number;
  path: string;
  visible?: boolean;
  isNew?: boolean;
}
export interface SidebarProps {
  items: SideNavBarItem[];
  activeItemKey: number;
  fixed?: boolean;
  onChange: (item: SideNavBarItem) => void;
  children?: ReactNode;
}

export const SidebarItem = ({
  items,
  activeItemKey,
  fixed,
  onChange,
  children = null,
  ...otherprops
}: SidebarProps) => {
  return (
    <StyledNavBarContainer>
      {children ? children : null}
      {items.map((item) => {
        const isActive = activeItemKey === item.id;
        return (
          <NavBarItemContainer
            key={item.analyticsKey}
            {...otherprops}
            className={`rk-nav-bar-item-container${isActive ? ' active' : ''}`}
            onClick={() => onChange(item)}
            data-cy={`sidebar-nav-${item.label.replace(/ /g, '-').toLowerCase()}`}
          >
            <Ripple />
            <StyledActiveIndicator isActive={isActive}>
              <SideBarItemIcon isActive={isActive} name={item.icon} />
              <NavBarItemLabel
                isActive={isActive}
                itemLabel={item.label}
              ></NavBarItemLabel>
            </StyledActiveIndicator>
            {item.isNew && (
              <AbsolutePositioned>
                <Badge
                  type="filled"
                  size="small"
                  label="New"
                  color="var(--md-sys-color-schemes-error)"
                />
              </AbsolutePositioned>
            )}
          </NavBarItemContainer>
        );
      })}
    </StyledNavBarContainer>
  );
};

interface NavBarItemContainerProps extends PropsWithChildren {
  className?: string;
  onClick: () => void;
  'data-cy'?: string;
}
const NavBarItemContainer = ({
  children,
  className = '',
  onClick,
  'data-cy': dataCy,
}: NavBarItemContainerProps) => {
  return (
    <StyledNavBarItemContainer className={className} onClick={onClick} data-cy={dataCy}>
      {children}
    </StyledNavBarItemContainer>
  );
};
const StyledNavBarItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: auto;
  width: calc(100% - 32px);
  justify-content: flex-start;
  padding: 4px 16px 4px 16px;
  position: relative;
  cursor: pointer;
`;

const StyledNavBarContainer = styled.div`
  width: 272px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  background-color: var(--md-ref-neutral-99);
  position: relative;
  z-index: 100;
  ${mq(BreakpointSizes.xs)} {
    display: none;
  }
`;
const AbsolutePositioned = styled.div`
  position: absolute;
  top: var(--rk-spacing-lg);
  right: var(--rk-spacing-lg);
`;

interface NavBarItemLabelProps extends PropsWithChildren {
  itemLabel?: string;
  isActive?: boolean;
}
const NavBarItemLabel = ({ isActive, itemLabel }: NavBarItemLabelProps) => {
  return (
    <Typography
      data-cy={itemLabel?.replace(/ /g, '-').toLowerCase()}
      variant={isActive ? 'textBodyLargeNeutral99' : 'textBodyLargeNeutral10'}
    >
      {itemLabel}
    </Typography>
  );
};
interface StyledActiveIndicatorProps {
  isActive?: boolean;
}
const StyledActiveIndicator = styled.div<StyledActiveIndicatorProps>`
  height: 28px;
  width: inherit;
  border-radius: 28px;
  background-color: ${(props) =>
    !props.isActive ? 'transparent' : 'var(--md-ref-primary-50)'};
  padding: 14px;
  display: flex;
  justify-content: left;
  align-items: center;
`;

interface NavBarItemIconProps {
  name: string;
  isActive?: boolean;
}
const SideBarItemIcon = ({ name, isActive = false }: NavBarItemIconProps) => {
  return (
    <Icon
      name={name}
      emotionCss={css`
        color: ${isActive
          ? 'var(--md-ref-neutral-99)'
          : 'var(--md-sys-color-on-surface-variant)'};
        margin-right: 16px;
      `}
    />
  );
};
